// Solutions-catalog bridge — shares the PKS solutions catalog data fetched by
// the host with canvas MFEs across the Module Federation boundary.
//
// Unscoped catalog (no application_ukey) powers the waffle menu.
// Scoped catalog (with application_ukey) is published after waffle selection
// for the Runs dropdown.

const WINDOW_KEY = '__SOLUTIONS_CATALOG__';
const SCOPED_WINDOW_KEY = '__SOLUTIONS_CATALOG_SCOPED__';
const EVENT_KEY = '__SOLUTIONS_CATALOG_UPDATED__';

export interface CatalogRuntimeAgentSnapshot {
  ukey: string;
  label?: string | null;
  definition?: string | null;
  is_solution?: boolean | null;
}

export interface CatalogItemSnapshot {
  solution: {
    ukey: string;
    label?: string | null;
    /** PKS application record ukey (e.g. daxl-app, gaxl-app, hcaxl-app). */
    application_ukey?: string | null;
  };
  runtime_agent?: CatalogRuntimeAgentSnapshot | null;
}

export interface SolutionsCatalogSnapshot {
  catalogItems: CatalogItemSnapshot[];
  moduleSolutionMap: Record<string, string[]>;
}

export interface ScopedSolutionsCatalogSnapshot {
  applicationUkey: string;
  catalogItems: CatalogItemSnapshot[];
}

function windowBag(): Record<string, unknown> | null {
  return typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>)
    : null;
}

function dispatchUpdated(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(EVENT_KEY));
  }
}

/** Publish unscoped catalog data (waffle). */
export function setSolutionsCatalog(snapshot: SolutionsCatalogSnapshot): void {
  const bag = windowBag();
  if (bag) {
    bag[WINDOW_KEY] = snapshot;
    dispatchUpdated();
  }
}

/** Publish application-scoped catalog after waffle module/solution selection. */
export function setScopedSolutionsCatalog(
  snapshot: ScopedSolutionsCatalogSnapshot,
): void {
  const bag = windowBag();
  if (bag) {
    bag[SCOPED_WINDOW_KEY] = snapshot;
    dispatchUpdated();
  }
}

/** Read the unscoped catalog snapshot. */
export function getSolutionsCatalog(): SolutionsCatalogSnapshot | null {
  const bag = windowBag();
  const value = bag?.[WINDOW_KEY];
  if (
    value &&
    typeof value === 'object' &&
    'catalogItems' in (value as Record<string, unknown>) &&
    Array.isArray((value as SolutionsCatalogSnapshot).catalogItems)
  ) {
    return value as SolutionsCatalogSnapshot;
  }
  return null;
}

/** Read the application-scoped catalog (Runs dropdown). */
export function getScopedSolutionsCatalog(): ScopedSolutionsCatalogSnapshot | null {
  const bag = windowBag();
  const value = bag?.[SCOPED_WINDOW_KEY];
  if (
    value &&
    typeof value === 'object' &&
    typeof (value as ScopedSolutionsCatalogSnapshot).applicationUkey === 'string' &&
    Array.isArray((value as ScopedSolutionsCatalogSnapshot).catalogItems)
  ) {
    return value as ScopedSolutionsCatalogSnapshot;
  }
  return null;
}

/** Clear catalogs (e.g. on logout or tenant switch). */
export function clearSolutionsCatalog(): void {
  const bag = windowBag();
  if (bag) {
    bag[WINDOW_KEY] = null;
    bag[SCOPED_WINDOW_KEY] = null;
  }
}
